import { useEffect, useState } from 'react';
import { Clock3 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { riderApi } from '../services/riderApi';

// APP-DEL-016. Work dispatched while a trip is under way is committed as the
// Rider's next batch; it becomes a new trip after this one is finished. Its
// pickup deadline runs meanwhile, so the trip screen says it is waiting and by
// when - otherwise it was only visible on Home and Assigned work.
function deadlineText(pickupBy, now) {
  if (!pickupBy) return null;
  const at = new Date(pickupBy);
  if (Number.isNaN(at.getTime())) return null;
  const minutes = Math.round((at.getTime() - now) / 60000);
  const clock = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (minutes < 0) return { late: true, text: `Pickup deadline passed at ${clock}. Your store has been told.` };
  return { late: minutes <= 15, text: `Pick up by ${clock} (${minutes} min left).` };
}

export default function RiderNextPickupNotice({ businessId, committedLoad, refreshKey }) {
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const waiting = Number(committedLoad) > 0;

  useEffect(() => {
    if (!waiting || !businessId) { setItems([]); return undefined; }
    const controller = new AbortController();
    riderApi.getAssignments(businessId, { status: 'COMMITTED', signal: controller.signal })
      .then((result) => setItems(Array.isArray(result?.items) ? result.items : []))
      .catch(() => setItems([]));
    return () => controller.abort();
  }, [businessId, waiting, refreshKey]);

  useEffect(() => {
    if (!waiting) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [waiting]);

  if (!waiting) return null;
  const count = Math.max(Number(committedLoad), items.length);
  const earliest = items.map((item) => item.pickupBy).filter(Boolean).sort()[0] || null;
  const deadline = deadlineText(earliest, now);

  return (
    <section
      data-testid="next-pickup"
      aria-label="Next pickup waiting"
      className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${deadline?.late ? 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100' : 'border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-100'}`}
    >
      <Clock3 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div>
        <p className="font-semibold">Next pickup waiting: {count} {count === 1 ? 'parcel' : 'parcels'}</p>
        <p className="mt-1">
          {deadline ? `${deadline.text} ` : ''}It becomes your next trip once you finish this one, so hand over any cash and finish the trip when your deliveries are done.
        </p>
        <Link to="/rider/assignments?view=committed" className="mt-2 inline-flex min-h-11 items-center font-semibold underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">View next pickup</Link>
      </div>
    </section>
  );
}
